import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Bottom-left floating social-proof pill.
 * Rotates through recent PAID Skip-The-Line + Tip purchases every 6s.
 * Data is anonymized server-side (no emails, no messages, only country).
 */
export const SalesTicker = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/public/sales-ticker?limit=12");
        if (cancelled) return;
        setItems(Array.isArray(r?.data?.items) ? r.data.items : []);
      } catch (_) {
        /* silent */
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6_000);
    return () => clearInterval(t);
  }, [items.length]);

  if (dismissed || items.length === 0) return null;

  const cur = items[idx % items.length];
  const label =
    cur.kind === "skip_the_line" ? "went VIP" : "left a tip";
  const where = cur.country_name ? `Someone in ${cur.country_name}` : "Someone";
  const when =
    cur.minutes_ago < 60
      ? `${cur.minutes_ago}m ago`
      : cur.minutes_ago < 60 * 24
      ? `${Math.floor(cur.minutes_ago / 60)}h ago`
      : `${Math.floor(cur.minutes_ago / (60 * 24))}d ago`;

  return (
    <div
      data-testid="sales-ticker"
      className="fixed bottom-4 left-4 z-40 max-w-[19rem] pointer-events-auto animate-fade-in"
    >
      <div className="border border-[#22c55e]/40 bg-black/85 backdrop-blur px-4 py-3 rounded-lg shadow-[0_0_25px_rgba(34,197,94,0.15)] flex items-center gap-3">
        <div className="h-2 w-2 rounded-full bg-[#22c55e] animate-pulse shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-white font-mono leading-snug">
            <span className="text-[#22c55e] font-bold">{where}</span>
            <span className="text-zinc-300"> {label} </span>
            <span className="text-white font-bold">${cur.amount.toFixed(2)}</span>
          </div>
          <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-400 font-mono mt-0.5">
            {when}
          </div>
        </div>
        <button
          type="button"
          data-testid="sales-ticker-close"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="text-zinc-500 hover:text-white text-xs leading-none px-1"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default SalesTicker;
